/**
 * Service for managing Notes API operations
 */

import { BaseService } from './BaseService';
import { parseUTCDate } from '../utils/dateUtils';

/**
 * Note Helper Functions
 */
export class NoteHelper {
    /**
     * Validate note data before submission
     * @param {Object} note - Note object to validate
     * @returns {Object} { valid: boolean, errors: string[] }
     */
    static validateNote(note) {
        const errors = [];

        // Title validation
        if (!note.title || typeof note.title !== 'string' || note.title.trim() === '') {
            errors.push('Title is required');
        } else if (note.title.length > 200) {
            errors.push('Title must not exceed 200 characters');
        }

        // Body validation
        if (!note.body || typeof note.body !== 'string' || note.body.trim() === '') {
            errors.push('Body is required');
        }

        // Priority validation
        if (!note.priorityId || note.priorityId <= 0) {
            errors.push('Priority is required and must be positive');
        }

        // Note Type validation
        if (!note.noteTypeId || note.noteTypeId <= 0) {
            errors.push('Note type is required and must be positive');
        }

        // Owner Type validation
        if (!note.ownerType || typeof note.ownerType !== 'string' || note.ownerType.trim() === '') {
            errors.push('Owner type is required');
        } else if (note.ownerType.length > 30) {
            errors.push('Owner type must not exceed 30 characters');
        }

        // Owner ID validation
        if (!note.ownerId || note.ownerId <= 0) {
            errors.push('Owner ID is required and must be positive');
        }

        // Effective From validation
        if (!note.effectiveFrom) {
            errors.push('Effective from date is required');
        }

        // Expiration vs Effective From validation
        if (note.expiration && note.effectiveFrom) {
            const effective = new Date(note.effectiveFrom);
            const expiration = new Date(note.expiration);
            if (expiration <= effective) {
                errors.push('Expiration must be after effective from date');
            }
        }

        return {
            valid: errors.length === 0,
            errors
        };
    }

    /**
     * Check if a note is currently active
     * @param {Object} note - Note object
     * @returns {boolean} True if note is active
     */
    static isNoteActive(note) {
        const now = new Date();
        const effectiveFrom = parseUTCDate(note.effectiveFrom);
        const expiration = note.expiration ? parseUTCDate(note.expiration) : null;

        return effectiveFrom <= now && (!expiration || expiration > now);
    }

    /**
     * Check if a note has expired
     * @param {Object} note - Note object
     * @returns {boolean} True if note has expired
     */
    static isNoteExpired(note) {
        if (!note.expiration) return false;
        return parseUTCDate(note.expiration) < new Date();
    }

    /**
     * Check if a note is scheduled for the future
     * @param {Object} note - Note object
     * @returns {boolean} True if note is scheduled
     */
    static isNoteScheduled(note) {
        const now = new Date();
        const effectiveFrom = parseUTCDate(note.effectiveFrom);
        return effectiveFrom > now;
    }

    /**
     * Get note status string
     * @param {Object} note - Note object
     * @returns {string} Status string ('active', 'expired', 'scheduled')
     */
    static getNoteStatus(note) {
        if (this.isNoteExpired(note)) return 'expired';
        if (this.isNoteActive(note)) return 'active';
        if (this.isNoteScheduled(note)) return 'scheduled';
        return 'unknown';
    }

    /**
     * Format date for display in local timezone
     * @param {string} dateString - ISO date string (UTC from backend)
     * @returns {string} Formatted date string in local time
     */
    static formatDate(dateString) {
        if (!dateString) return 'N/A';
        try {
            // Create Date object which will parse UTC string from backend
            const date = new Date(dateString);
            // Check if date is valid
            if (isNaN(date.getTime())) {
                console.error('Invalid date string:', dateString);
                return 'Invalid Date';
            }
            // toLocaleString() automatically converts to local timezone
            return date.toLocaleString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return 'Error';
        }
    }

    /**
     * Format date for datetime-local input (in local timezone)
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted date string for input in local time
     */
    static formatDateForInput(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        // Format as YYYY-MM-DDTHH:mm in local timezone
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    /**
     * Get current datetime for datetime-local input (in local timezone)
     * @returns {string} Current datetime formatted for input in local time
     */
    static getCurrentDateTimeForInput() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    /**
     * Convert local datetime-local input value to UTC ISO string
     * @param {string} localDateTimeString - Local datetime string from input (YYYY-MM-DDTHH:mm)
     * @returns {string} UTC ISO string
     */
    static convertLocalToUTC(localDateTimeString) {
        return convertLocalInputToUTC(localDateTimeString);
    }

    /**
     * Sort notes by priority weight (descending) then creation date (descending)
     * @param {Array} notes - Array of note objects
     * @returns {Array} Sorted array
     */
    static sortByPriority(notes) {
        return [...notes].sort((a, b) => {
            // Higher priority weight first
            if (a.priorityWeight !== b.priorityWeight) {
                return (b.priorityWeight || 0) - (a.priorityWeight || 0);
            }
            // Newer notes first
            return parseUTCDate(b.createdAt) - parseUTCDate(a.createdAt);
        });
    }

    /**
     * Sort notes by creation date
     * @param {Array} notes - Array of note objects
     * @param {string} order - 'asc' or 'desc'
     * @returns {Array} Sorted array
     */
    static sortByDate(notes, order = 'desc') {
        return [...notes].sort((a, b) => {
            const dateA = parseUTCDate(a.createdAt);
            const dateB = parseUTCDate(b.createdAt);
            return order === 'asc' ? dateA - dateB : dateB - dateA;
        });
    }

    /**
     * Filter notes by status
     * @param {Array} notes - Array of note objects
     * @param {string} status - 'all', 'active', 'expired', 'scheduled', 'alerts'
     * @returns {Array} Filtered array
     */
    static filterByStatus(notes, status) {
        if (status === 'all') return notes;
        if (status === 'alerts') return notes.filter(note => note.isAlert);
        if (status === 'active') return notes.filter(note => this.isNoteActive(note));
        if (status === 'expired') return notes.filter(note => this.isNoteExpired(note));
        if (status === 'scheduled') return notes.filter(note => this.isNoteScheduled(note));
        return notes;
    }
}

/**
 * Owner Type Constants
 */
export const OwnerType = {
    CLIENT: 'Client',
    SAMPLE: 'Sample',
    CONTACT: 'Contact',
    PROJECT: 'Project',
    ORDER: 'Order'
};

/**
 * Note Service Class
 */
class NoteService extends BaseService {
    constructor() {
        super('/api/v1/notes');
    }

    /**
     * Get all notes
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of Note objects
     */
    async getAll(accessToken) {
        try {
            return await this.makeRequest(accessToken, '');
        } catch (error) {
            console.error('Failed to fetch notes:', error);
            throw error;
        }
    }

    /**
     * Get note by ID - uses BaseService implementation
     */
    // Inherited from BaseService

    /**
     * Get notes by owner
     * @param {string} accessToken - JWT access token
     * @param {string} ownerType - Type of owner (e.g., "Client", "Sample")
     * @param {number} ownerId - ID of the owner
     * @returns {Promise<Array>} Array of Note objects
     */
    async getByOwner(accessToken, ownerType, ownerId) {
        try {
            return await this.makeRequest(accessToken, `/owner/${ownerType}/${ownerId}`);
        } catch (error) {
            console.error(`Failed to fetch notes for ${ownerType} ${ownerId}:`, error);
            throw error;
        }
    }

    /**
     * Get active notes
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of active Note objects
     */
    async getActive(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/active');
        } catch (error) {
            console.error('Failed to fetch active notes:', error);
            throw error;
        }
    }

    /**
     * Get active notes by owner
     * @param {string} accessToken - JWT access token
     * @param {string} ownerType - Type of owner
     * @param {number} ownerId - ID of the owner
     * @returns {Promise<Array>} Array of active Note objects
     */
    async getActiveByOwner(accessToken, ownerType, ownerId) {
        try {
            return await this.makeRequest(accessToken, `/active/owner/${ownerType}/${ownerId}`);
        } catch (error) {
            console.error(`Failed to fetch active notes for ${ownerType} ${ownerId}:`, error);
            throw error;
        }
    }

    /**
     * Get alert notes
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of alert Note objects
     */
    async getAlerts(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/alerts');
        } catch (error) {
            console.error('Failed to fetch alert notes:', error);
            throw error;
        }
    }

    /**
     * Get alert notes by owner
     * @param {string} accessToken - JWT access token
     * @param {string} ownerType - Type of owner
     * @param {number} ownerId - ID of the owner
     * @returns {Promise<Array>} Array of alert Note objects
     */
    async getAlertsByOwner(accessToken, ownerType, ownerId) {
        try {
            return await this.makeRequest(accessToken, `/alerts/owner/${ownerType}/${ownerId}`);
        } catch (error) {
            console.error(`Failed to fetch alerts for ${ownerType} ${ownerId}:`, error);
            throw error;
        }
    }

    /**
     * Get notes by priority
     * @param {string} accessToken - JWT access token
     * @param {number} priorityId - Priority ID
     * @returns {Promise<Array>} Array of Note objects
     */
    async getByPriority(accessToken, priorityId) {
        try {
            return await this.makeRequest(accessToken, `/priority/${priorityId}`);
        } catch (error) {
            console.error(`Failed to fetch notes by priority ${priorityId}:`, error);
            throw error;
        }
    }

    /**
     * Get notes by type
     * @param {string} accessToken - JWT access token
     * @param {number} noteTypeId - Note type ID
     * @returns {Promise<Array>} Array of Note objects
     */
    async getByType(accessToken, noteTypeId) {
        try {
            return await this.makeRequest(accessToken, `/type/${noteTypeId}`);
        } catch (error) {
            console.error(`Failed to fetch notes by type ${noteTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Get expiring notes
     * @param {string} accessToken - JWT access token
     * @param {number} days - Number of days ahead to check (default: 7)
     * @returns {Promise<Array>} Array of Note objects expiring soon
     */
    async getExpiring(accessToken, days = 7) {
        try {
            return await this.makeRequest(accessToken, `/expiring?days=${days}`);
        } catch (error) {
            console.error(`Failed to fetch expiring notes:`, error);
            throw error;
        }
    }

    /**
     * Create a new note with validation
     * @param {string} accessToken - JWT access token
     * @param {Object} note - Note object without ID
     * @returns {Promise<Object>} Created Note object
     */
    async create(accessToken, note) {
        const validation = NoteHelper.validateNote(note);
        if (!validation.valid) {
            throw new Error(validation.errors.join(', '));
        }
        return super.create(accessToken, note);
    }

    /**
     * Update an existing note with validation
     * @param {string} accessToken - JWT access token
     * @param {number} id - Note ID
     * @param {Object} note - Updated Note object
     * @returns {Promise<Object>} Updated Note object
     */
    async update(accessToken, id, note) {
        const validation = NoteHelper.validateNote(note);
        if (!validation.valid) {
            throw new Error(validation.errors.join(', '));
        }
        return super.update(accessToken, id, note);
    }

    /**
     * Delete a note - uses BaseService implementation
     */
    // Inherited from BaseService

    /**
     * Update note priority
     * @param {string} accessToken - JWT access token
     * @param {number} id - Note ID
     * @param {number} priorityId - New priority ID
     * @returns {Promise<void>}
     */
    async updatePriority(accessToken, id, priorityId) {
        try {
            return await this.makeRequest(
                accessToken,
                `/${id}/priority/${priorityId}`,
                'PATCH'
            );
        } catch (error) {
            console.error(`Failed to update priority for note ${id}:`, error);
            throw error;
        }
    }

    /**
     * Update note type
     * @param {string} accessToken - JWT access token
     * @param {number} id - Note ID
     * @param {number} noteTypeId - New note type ID
     * @returns {Promise<void>}
     */
    async updateType(accessToken, id, noteTypeId) {
        try {
            return await this.makeRequest(
                accessToken,
                `/${id}/type/${noteTypeId}`,
                'PATCH'
            );
        } catch (error) {
            console.error(`Failed to update type for note ${id}:`, error);
            throw error;
        }
    }

    /**
     * Set alert flag
     * @param {string} accessToken - JWT access token
     * @param {number} id - Note ID
     * @param {boolean} isAlert - Alert flag value
     * @returns {Promise<void>}
     */
    async setAlert(accessToken, id, isAlert) {
        try {
            return await this.makeRequest(
                accessToken,
                `/${id}/alert/${isAlert}`,
                'PATCH'
            );
        } catch (error) {
            console.error(`Failed to set alert for note ${id}:`, error);
            throw error;
        }
    }

    /**
     * Extend note expiration
     * @param {string} accessToken - JWT access token
     * @param {number} id - Note ID
     * @param {number} days - Number of days to extend (default: 7)
     * @returns {Promise<Object>} Updated Note object
     */
    async extendExpiration(accessToken, id, days = 7) {
        try {
            return await this.makeRequest(
                accessToken,
                `/${id}/extend?days=${days}`,
                'PATCH'
            );
        } catch (error) {
            console.error(`Failed to extend expiration for note ${id}:`, error);
            throw error;
        }
    }
}

// Export singleton instance
export const noteService = new NoteService();

// Export individual methods for convenience
export const {
    getAll: getAllNotes,
    getById: getNoteById,
    getByOwner: getNotesByOwner,
    getActive: getActiveNotes,
    getActiveByOwner: getActiveNotesByOwner,
    getAlerts: getAlertNotes,
    getAlertsByOwner: getAlertNotesByOwner,
    getByPriority: getNotesByPriority,
    getByType: getNotesByType,
    getExpiring: getExpiringNotes,
    create: createNote,
    update: updateNote,
    delete: deleteNote,
    updatePriority: updateNotePriority,
    updateType: updateNoteType,
    setAlert: setNoteAlert,
    extendExpiration: extendNoteExpiration
} = noteService;

export default noteService;
